/* eslint-disable @typescript-eslint/no-explicit-any */
import { cn } from "@/lib/utils";
import {
  AllCommunityModule,
  ModuleRegistry,
  RowSelectionOptions,
  type ColDef,
  type GridOptions,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo, useState, useCallback, useRef } from "react";
import React from "react";
import CustomPagination from "../pagination/custom-pagination";
import { Loader2 } from "lucide-react";

// Register modules once at import time to prevent multiple registrations
ModuleRegistry.registerModules([AllCommunityModule]);

interface DataTableProps<T> {
  rowData: T[];
  columnDefs: ColDef<T>[];
  gridOptions?: GridOptions<T>;
  defaultColDef?: ColDef<T>;
  height?: number;
  className?: string;
  showPagination?: boolean;
  paginationProps?: PaginationProps;
  onSelectionChanged?: (event: any) => void;
  rowSelection?: RowSelectionOptions<T>;
  onRowClicked?: (params: any) => void;
  loading?: boolean;
  rowsPerPage?: number;
  onRowsPerPageChange?: (limit: number) => void;
  rowHeight?: number;
}

interface PaginationProps {
  totalEntries?: number;
  pageSize: number;
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  className?: string;
  rowsPerPage?: number;
  onRowsPerPageChange?: (limit: number) => void;
}

// Custom hook for stable row ID generation
const useStableRowIds = (rowData: any[]) => {
  return useMemo(() => {
    const idMap = new Map();
    
    return rowData.map((row, index) => {
      let rowId: string;
      
      // Priority 1: Use existing unique IDs
      if (row._id) {
        rowId = String(row._id);
      } else if (row.id) {
        rowId = String(row.id);
      } else if (row.domain) {
        // Priority 2: Create composite key
        const compositeKey = [
          row.domain,
          row.lastChecked || row.timestamp,
          row.overallScore,
          row.espType
        ].filter(Boolean).join('-');
        
        if (compositeKey) {
          // Deterministic hash
          let hash = 0;
          for (let i = 0; i < compositeKey.length; i++) {
            const char = compositeKey.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
          }
          rowId = `composite-${Math.abs(hash)}`;
        } else {
          // Priority 3: Index-based fallback
          rowId = `index-${index}`;
        }
      } else {
        // Priority 4: Last resort
        rowId = `fallback-${index}`;
      }
      
      // Ensure uniqueness
      if (idMap.has(rowId)) {
        rowId = `${rowId}-${index}`;
      }
      idMap.set(rowId, true);
      
      return { ...row, __agGridId: rowId };
    });
  }, [rowData]);
};

/**
 * A reusable, type-safe AG Grid table component with advanced performance optimizations.
 */
const DataTable = React.forwardRef<AgGridReact<any>, DataTableProps<any>>(
  (
    {
      rowData = [],
      columnDefs = [],
      height = 500,
      className,
      showPagination = false,
      paginationProps,
      defaultColDef,
      loading = false,
      onSelectionChanged,
      onRowClicked,
      rowHeight = 40,
      rowSelection,
    },
    ref
  ) => {
    const gridRef = useRef<AgGridReact>(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    
    // Use stable row IDs
    const stableRowData = useStableRowIds(rowData);

    // Safely create a deep copy of column definitions to prevent reference issues
    const safeColumnDefs = useMemo(() => {
      if (!columnDefs || !Array.isArray(columnDefs)) return [];

      return columnDefs.map((col) => {
        if (!col) return {};
        return { ...col };
      });
    }, [columnDefs]);

    // Default column definition with fallbacks and performance optimizations
    const defaultColDefMemo = useMemo<ColDef>(() => {
      return {
        flex: 1,
        filter: true,
        editable: false,
        sortable: true,
        headerStyle: { fontSize: "12px" },
        resizable: true,
        suppressSizeToFit: true,
        ...defaultColDef,
      };
    }, [defaultColDef]);

    const memoizedRowSelection = useMemo((): RowSelectionOptions => {
      if (!rowSelection)
        return {
          mode: "multiRow",
          headerCheckbox: true,
        };
      return rowSelection;
    }, [rowSelection]);

    // Safe grid options with performance optimizations
    const gridOptionsMemo = useMemo<GridOptions>(() => {
      return {
        // Core options
        rowBuffer: 1000,
        rowModelType: "clientSide",
        animateRows: false,
        suppressPropertyNamesCheck: true,

        // Disable row virtualization to show all rows at once
        suppressRowVirtualisation: true,
        suppressColumnVirtualisation: true,

        // Performance optimizations
        enableCellTextSelection: false,
        suppressRowDeselection: true,
        cacheQuickFilter: true,

        // Ensure data loads completely on initial render
        suppressModelUpdateAfterUpdateTransaction: true,
        suppressScrollOnNewData: false,

        // DOM element reuse
        suppressAnimationFrame: true,
        suppressColumnMoveAnimation: true,

        // FIXED: Deterministic and unique row ID generation
        getRowId: (params) => {
          if (!params || !params.data) return `fallback-${Math.random()}-${Date.now()}`;
          
          // Priority 1: Use existing unique IDs
          if (params.data._id) return String(params.data._id);
          if (params.data.id) return String(params.data.id);
          
          // Priority 2: Use testId for placement tests (most unique identifier)
          // This ensures uniqueness when same email has multiple tests (B2B and B2C merged)
          if (params.data.testId) {
            return `test-${params.data.testId}`;
          }
          
          // Priority 3: Use email + type combination if testId not available but type exists
          // This handles cases where same email might appear in different contexts
          if (params.data.email && params.data.type) {
            return `email-${params.data.email}-${params.data.type}`;
          }
          
          // Priority 4: Use email as unique identifier (fallback for non-placement tests)
          if (params.data.email) {
            return `email-${params.data.email}`;
          }
          
          // Priority 5: Use domain with timestamp
          if (params.data.domain) {
            const timestamp = params.data.lastChecked || params.data.timestamp || Date.now();
            const hash = JSON.stringify(params.data).split('').reduce((a, b) => {
              a = ((a << 5) - a) + b.charCodeAt(0);
              return a & a;
            }, 0);
            return `domain-${params.data.domain}-${timestamp}-${Math.abs(hash)}`;
          }
          
          // Priority 6: Last resort with guaranteed uniqueness
          return `fallback-${Math.random()}-${Date.now()}-${Math.random()}`;
        },

        // Batch updates
        suppressFieldDotNotation: true,
        enableCellChangeFlash: false,

        // Pagination settings
        pagination: false,
      };
    }, []);

    // Grid ready handler with performance optimizations
    const onGridReady = useCallback(() => {
      if (gridRef.current?.api) {
        // Force a complete refresh of the grid
        setTimeout(() => {
          if (gridRef.current?.api) {
            // Ensure all rows are rendered
            gridRef.current.api.redrawRows();
            // Size columns to fit
            gridRef.current.api.sizeColumnsToFit();
          }

          setIsInitialLoad(false);
        }, 100);
      }
    }, [stableRowData]);

    return (
      <div className="w-full" >
        <div
          style={{ width: "100%", height }}
          className={cn("di-ag-grid relative", className)}
        >
          {loading && (
            <div className="absolute inset-0 bg-background/60 backdrop-blur-sm z-10 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
                <p className="text-sm text-muted-foreground">Loading data...</p>
              </div>
            </div>
          )}

          {/* Main AG Grid component with performance optimizations */}
          <AgGridReact
            ref={(instance) => {
              // Set both refs with null checks
              if (ref) {
                if (typeof ref === "function") {
                  ref(instance);
                } else {
                  ref.current = instance;
                }
              }
              gridRef.current = instance;
            }}
            rowData={stableRowData} // Use stable row data instead of original
            columnDefs={safeColumnDefs}
            defaultColDef={defaultColDefMemo}
            pagination={false}
            rowHeight={rowHeight}
            rowSelection={memoizedRowSelection}
            onSelectionChanged={onSelectionChanged}
            onRowClicked={onRowClicked}
            onGridReady={onGridReady}
            suppressMovableColumns={true}
            suppressLoadingOverlay={true}
            suppressNoRowsOverlay={true}
            suppressPaginationPanel={true}
            enableCellTextSelection={false}
            suppressRowDeselection={true}
            suppressRowVirtualisation={true}
            suppressColumnVirtualisation={true}
            alwaysShowVerticalScroll={true}
            suppressScrollOnNewData={false}
            {...gridOptionsMemo}
          />
        </div>

        {showPagination && !isInitialLoad && (
          <CustomPagination
            currentPage={paginationProps?.currentPage ?? 1}
            onChange={paginationProps?.onPageChange ?? (() => {})}
            totalPages={paginationProps?.totalPages ?? 10}
            siblingsCount={1}
            className={cn("mt-4", paginationProps?.className)}
            onRowsPerPageChange={
              paginationProps?.onRowsPerPageChange ?? (() => {})
            }
            rowsPerPage={paginationProps?.rowsPerPage ?? 100}
            totalEntries={paginationProps?.totalEntries ?? 0}
          />
        )}
      </div>
    );
  }
);

// Add display name for better debugging
DataTable.displayName = "DataTable";

export default DataTable;
